import React, { useState, useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import ToggleSwitch from '../ToggleSwitch/ToggleSwitch';
import SettingsIcon from '../../../images/vector/settings.svg';
import { ApolloContext } from '../../../ApolloContextProvider';
import { SPACE } from '../../../constants/keyboardCodes';
import { CAPTIONS, OWL_ANIMATION, DEFAULT_FONT_SIZE_SCALE } from '../../../constants/accessibility';
import Tooltip from './Tooltip';
import selectors from '../../../rootSelectors';
import { LANGUAGES } from '../../localization/consts';

const AccessibilitySettingsDropdown = ({
    showSettingsPopup,
    isMazeLicensed
}) => {
    const {
        isLouisianaLicenseAvailable,
        setAccessibilityConfigValues,
        isCaptionsEnabled,
        isAnimationVisualReduced,
        fontSizeScaleValue
    } = useContext(ApolloContext);
    const [isDropdownVisible, showDropdown] = useState(false);
    const menuRef = useRef();
    const settingsIconRef = useRef();
    const [loadingState, setLoadingState] = useState({});
    const language = useSelector(selectors.getCurrentLanguage);
    const zoomInEnabled = window.amplify_lantern_config.isAccessibilityEnabled;
    const shouldEnableZoom = (language === LANGUAGES.ENGLISH) && (String(zoomInEnabled) === 'true' || (isLouisianaLicenseAvailable && isMazeLicensed));

    const hideDropdown = (e = null) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        showDropdown(false);
    };

    const handleClickOutside = (event) => {
        if (isDropdownVisible && (
            menuRef.current && !menuRef.current.contains(event.target) ||
            settingsIconRef.current && settingsIconRef.current.contains(event.target))
        ) {
            hideDropdown(event);
        }
    };

    useEffect(() => {
        document.addEventListener("click", handleClickOutside, true);
        return () => {
            document.removeEventListener("click", handleClickOutside, true);
        };
    });

    const resetZoomValue = () => {
        if (!shouldEnableZoom) {
            if (fontSizeScaleValue !== DEFAULT_FONT_SIZE_SCALE) {
                setAccessibilityConfigValues({ fontSizeScaleValue: DEFAULT_FONT_SIZE_SCALE });
            }
        }
    };

    useEffect(() => {
        resetZoomValue();
    }, []);

    const showSettingsPopupHandler = () => {
        showSettingsPopup();
        showDropdown(false);
    };

    const onDropdownClick = (e) => {
        e.preventDefault();
        if (isDropdownVisible) {
            hideDropdown(e);
        }
        else {
            showDropdown(true);
        }
    };

    const onDropdownKeyDown = (e) => {
        if (e.keyCode !== SPACE) {
            return;
        }
        e.preventDefault();
        if (isDropdownVisible) {
            hideDropdown(e);
        }
        else {
            showDropdown(true);
        }
    };

    const toggleLoadingState = (id, state) => {
        setLoadingState(prevState => ({ ...prevState, [id]: state }));
    };

    const toggleCaptionsVisibility = (val) => {
        toggleLoadingState(CAPTIONS, true);
        setAccessibilityConfigValues({ isCaptionsEnabled: val }).finally(() => {
            toggleLoadingState(CAPTIONS, false);
        });
    };

    const toggleOwlAnimation = (val) => {
        toggleLoadingState(OWL_ANIMATION, true);
        setAccessibilityConfigValues({ isAnimationVisualReduced: !val })
            .finally(() => {
                toggleLoadingState(OWL_ANIMATION, false);
            });
    };

    return (
        <div className="settings">
            <button className="settings-icon-button" aria-label="settings" ref={settingsIconRef} onClick={onDropdownClick} onKeyDown={onDropdownKeyDown}>
                <SettingsIcon className="settings-icon" />
            </button>
            <div className="menu-container">
                <div ref={menuRef} className={`dropdown-menu ${isDropdownVisible ? 'active' : 'inactive'}`}>
                    <ul>
                        {
                            shouldEnableZoom &&
                            (
                                <li>
                                    <button className="dropdown-item" onClick={showSettingsPopupHandler}>
                                        Maze Text Size
                                        <svg
                                            width="23" height="22" viewBox="0 0 23 22" fill="none"
                                            xmlns="http://www.w3.org/2000/svg">
                                            <path
                                                d="M6.34 3.547 7.636 2.25l8.75 8.75-8.75 8.751-1.296-1.296 7.452-7.454L6.34 3.547z"
                                                fill="currentColor" />
                                        </svg>
                                    </button>
                                </li>
                            )
                        }
                        <li>
                            Closed Captioning
                            <ToggleSwitch
                                id="captions"
                                isDisabled={loadingState.captions} checked={isCaptionsEnabled}
                                onChange={(val) => toggleCaptionsVisibility(val)} />
                        </li>
                        {
                            window.amplify_lantern_config.isAccessibilityEnabled === 'true' &&
                            (
                                <li>
                                    <Tooltip
                                        text={
                                            isAnimationVisualReduced
                                                ? "Enable animation"
                                                : "Disable animation"
                                        }
                                    >
                                        Animation
                                        <ToggleSwitch
                                            id="owlAnimation"
                                            isDisabled={loadingState.owlAnimation}
                                            checked={!isAnimationVisualReduced}
                                            onChange={toggleOwlAnimation}
                                        />
                                    </Tooltip>
                                </li>
                            )
                        }
                    </ul>
                </div>
            </div>
        </div>
    );
};
AccessibilitySettingsDropdown.propTypes = {
    showSettingsPopup: PropTypes.func,
    isMazeLicensed: PropTypes.bool
};
AccessibilitySettingsDropdown.defaultProps = {
    showSettingsPopup: () => { },
    isMazeLicensed: false
};

export default AccessibilitySettingsDropdown;
